import { Card, Col, ProgressBar, Stack } from "react-bootstrap";

import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useMemo } from "react";
import LoadingIndicator from "../LoadingIndicator";
import ErrorResponse from "../ErrorResponse";
import moment from "moment/min/moment-with-locales";
import { ArrowDown, ArrowUp, GenderFemale, GenderMale } from "react-bootstrap-icons";
import { doughnutOptions, htmlLegendPlugin } from "../chartOptions";
import { toAutoDecimals } from "../HelperFunctions";

ChartJS.register(ArcElement, Tooltip, Legend, annotationPlugin, ChartDataLabels);

export default function ListenerGroupProfile({ data, loading, error, dataType, header }) {
    const mobilityChartData = useMemo(() => {
        if (data && dataType === 1) {
            const hasApp = data.mobility.own_app > 1 || data.mobility_avg.own_app > 1;
            const labels = ["PC", "Smart Speaker", hasApp ? "Mobile (No App)" : "Mobile"];
            if (hasApp) {
                labels.push("Mobile (App)");
            }
            return {
                labels: labels,
                datasets: [
                    {
                        label: "Latest Week",
                        type: "doughnut",
                        backgroundColor: ["#40775A", "#A3CCB6", "#519872", "#67CB88"],
                        data: [
                            data.mobility.pc,
                            data.mobility.smart_speaker,
                            data.mobility.mobile,
                            hasApp ? data.mobility.own_app : "N/A",
                        ],
                        datalabels: {
                            anchor: "end",
                        },
                    },
                    {
                        label: "Average",
                        type: "doughnut",
                        backgroundColor: ["#40775A", "#A3CCB6", "#519872", "#67CB88"],
                        data: [
                            data.mobility_avg.pc,
                            data.mobility_avg.smart_speaker,
                            data.mobility_avg.mobile,
                            hasApp ? data.mobility_avg.own_app : "N/A",
                        ],
                        datalabels: {
                            anchor: "start",
                        },
                    },
                ],
            };
        }
    }, [data, dataType]);

    return (
        <Card>
            <Card.Header className="text-center">{header}</Card.Header>
            <Card.Body className="d-flex flex-column pb-1 justify-content-center">
                {loading ? (
                    <LoadingIndicator width={75} />
                ) : error ? (
                    <ErrorResponse errorCode={error} />
                ) : (
                    data && (
                        <>
                            {dataType === 1 ? (
                                <Col className="d-flex">
                                    <Stack id={`legend-container-lg-profile-${header}`} className="py-3"></Stack>
                                    <div className="dashboard-chart-lg-profile">
                                        <Doughnut
                                            data={mobilityChartData}
                                            options={doughnutOptions(`legend-container-lg-profile-${header}`)}
                                            plugins={[htmlLegendPlugin]}
                                        />
                                    </div>
                                </Col>
                            ) : (
                                <>
                                    <Col className=" align-self-center fs-6 bg-ra-dark-blue text-light rounded-1 px-3 py-1 mb-2">
                                        <span>Average age: </span>
                                        <span className="fw-bold">{Math.round(data.average_age)}</span>
                                    </Col>
                                    <Col className="d-flex gap-3 mb-2 align-items-center">
                                        <Col xs="auto" className="d-flex flex-column align-items-center">
                                            <GenderMale height={30} width={30} />
                                            <span>Male</span>
                                        </Col>
                                        <Col>
                                            <ProgressBar className="gender-bar">
                                                <ProgressBar
                                                    className="bg-ra-alternative-blue fw-bold fs-7"
                                                    label={`${toAutoDecimals(data.male, 1)}%`}
                                                    now={data.male}
                                                    key={1}
                                                />
                                                <ProgressBar
                                                    className="bg-ra-brown fw-bold fs-7"
                                                    label={`${toAutoDecimals(data.female, 1)}%`}
                                                    now={data.female}
                                                    key={2}
                                                />
                                            </ProgressBar>
                                        </Col>
                                        <Col xs="auto" className="d-flex flex-column align-items-center">
                                            <GenderFemale height={30} width={30} />
                                            <span>Female</span>
                                        </Col>
                                    </Col>
                                </>
                            )}
                            <Col>
                                <Stack direction="horizontal">
                                    <Col>
                                        <Stack>
                                            <Stack direction="horizontal" className="fw-bold fs-3 text-center justify-content-center">
                                                {data.tsl_session && data.tsl_session_avg ? (
                                                    data.tsl_session > data.tsl_session_avg ? (
                                                        <ArrowUp className="text-success col-2" />
                                                    ) : (
                                                        <ArrowDown className="text-danger col-2" />
                                                    )
                                                ) : (
                                                    <div className="col-2"></div>
                                                )}
                                                <span>
                                                    {data.tsl_session
                                                        ? moment.utc(moment.duration(data.tsl_session, "s").asMilliseconds()).format("HH:mm:ss")
                                                        : "--:--:--"}
                                                </span>
                                                <div className="col-2"></div>
                                            </Stack>
                                            <span className="text-center fs-6">TSL per Session</span>
                                            <span className="text-center text-muted fst-italic fs-8">
                                                <span>AVG </span>(
                                                {data.tsl_session_avg
                                                    ? moment.utc(moment.duration(data.tsl_session_avg, "s").asMilliseconds()).format("HH:mm:ss")
                                                    : "--:--:--"}
                                                )
                                            </span>
                                        </Stack>
                                    </Col>
                                    <div className="vr"></div>
                                    <Col>
                                        <Stack>
                                            <Stack direction="horizontal" className="fw-bold fs-3 text-center justify-content-center">
                                                {data.tsl_listener && data.tsl_listener_avg ? (
                                                    data.tsl_listener > data.tsl_listener_avg ? (
                                                        <ArrowUp className="text-success col-2" />
                                                    ) : (
                                                        <ArrowDown className="text-danger col-2" />
                                                    )
                                                ) : (
                                                    <div className="col-2"></div>
                                                )}
                                                <span>
                                                    {data.tsl_listener
                                                        ? moment.utc(moment.duration(data.tsl_listener, "s").asMilliseconds()).format("HH:mm:ss")
                                                        : "--:--:--"}
                                                </span>
                                                <div className="col-2"></div>
                                            </Stack>
                                            <span className="text-center fs-6">TSL per Listener</span>
                                            <span className="text-center text-muted fst-italic fs-8">
                                                <span>AVG </span>(
                                                {data.tsl_listener_avg
                                                    ? moment.utc(moment.duration(data.tsl_listener_avg, "s").asMilliseconds()).format("HH:mm:ss")
                                                    : "--:--:--"}
                                                )
                                            </span>
                                        </Stack>
                                    </Col>
                                </Stack>
                            </Col>
                        </>
                    )
                )}
            </Card.Body>
        </Card>
    );
}
